import {
  typeConverter,
  useFirestore,
  useFirestoreResults,
  useFirestoreSnapshot,
} from '@goschool/react-firebase'
import type {User} from 'firebase/auth'
import type {DocumentSnapshot, QueryDocumentSnapshot} from 'firebase/firestore'
import {
  collection,
  collectionGroup,
  doc,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore'
import {useMemo} from 'react'
import type {Course} from '../Course'
import type {CourseChat} from '../CourseChat'
import type {GoSchoolUser} from '../GoSchoolUser'
import type {Organization} from '../Organization'
import {courseConverter, organizationConverter} from '../converters'

export function useChat(organizationId?: string, courseId?: string, chatId?: string) {
  const firestore = useFirestore()
  const ref = useMemo(() => {
    if (organizationId != null && courseId != null && chatId != null) {
      return doc(
        firestore,
        'organizations',
        organizationId,
        'courses',
        courseId,
        'chats',
        chatId
      ).withConverter(typeConverter<CourseChat>())
    }
    return undefined
  }, [firestore, organizationId, courseId, chatId])

  return useFirestoreSnapshot(ref)
}

export function useCourseChats(
  courseSnapshot: QueryDocumentSnapshot<Course> | null | undefined,
  max: number | undefined = undefined
) {
  const q = useMemo(() => {
    if (courseSnapshot == null) {
      return undefined
    }
    const base = query(
      collection(courseSnapshot.ref, 'chats').withConverter(
        typeConverter<CourseChat>()
      ),
      orderBy('updated_at', 'desc')
    )
    return max != null ? query(base, limit(max)) : base
  }, [courseSnapshot, max])

  return useFirestoreResults(q)
}

export function useUserCourseChats(
  courseSnapshot: QueryDocumentSnapshot<Course> | null | undefined,
  user: User | null | undefined,
  max: number | undefined = undefined
) {
  const q = useMemo(() => {
    if (courseSnapshot == null || user === undefined) {
      return undefined
    }
    if (user == null) {
      return undefined
    }
    const base = query(
      collection(courseSnapshot.ref, 'chats').withConverter(
        typeConverter<CourseChat>()
      ),
      where('participants', 'array-contains', user.uid),
      orderBy('updated_at', 'desc')
    )
    return max != null ? query(base, limit(max)) : base
  }, [courseSnapshot, user, max])

  const results = useFirestoreResults(q)
  return user == null ? [] : results
}

export function useAllChats(max = 200) {
  const firestore = useFirestore()
  const q = useMemo(
    () =>
      query(
        collectionGroup(firestore, 'chats').withConverter(
          typeConverter<CourseChat>()
        ),
        orderBy('updated_at', 'desc'),
        limit(max)
      ),
    [firestore, max]
  )
  return useFirestoreResults(q)
}

export function useChatCreatedBy(chat: CourseChat | null | undefined) {
  const firestore = useFirestore()
  const createdBy = chat?.created_by ?? null
  const isAnonymous = chat?.anonymous ?? true

  const ref = useMemo(() => {
    if (createdBy == null || isAnonymous) {
      return undefined
    }
    return doc(firestore, 'users', createdBy).withConverter(
      typeConverter<GoSchoolUser>()
    )
  }, [firestore, createdBy, isAnonymous])

  const snapshot = useFirestoreSnapshot(ref)
  return createdBy == null || isAnonymous ? null : snapshot
}

export function useChatCourse(
  chatSnapshot: DocumentSnapshot<CourseChat> | null | undefined
) {
  const courseRef = useMemo(() => {
    if (chatSnapshot == null) {
      return undefined
    }
    const ref = chatSnapshot.ref.parent.parent
    return ref?.withConverter(courseConverter)
  }, [chatSnapshot])
  return useFirestoreSnapshot(courseRef, {watch: false})
}

export function useChatOrganization(
  chatSnapshot: DocumentSnapshot<CourseChat> | null | undefined
) {
  const orgRef = useMemo(() => {
    if (chatSnapshot == null) {
      return undefined
    }
    const courseRef = chatSnapshot.ref.parent.parent
    const org = courseRef?.parent.parent
    return org?.withConverter(organizationConverter)
  }, [chatSnapshot])
  return useFirestoreSnapshot(orgRef, {watch: false})
}
